import { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardActionArea,
  Radio,
  FormHelperText,
  TextField,
  FormControl,
  FormLabel,
} from "@mui/material";
import {
  Control,
  Controller,
  FieldError,
  FieldPath,
  FieldValues,
  useController,
  useFormContext,
  UseFormRegisterReturn,
} from "react-hook-form";
import { useTranslation } from "react-i18next";

import { EtisalatIcon, OrangeIcon, VodafoneIcon, WeIcon } from "../../../../../assets/icons/MUI/PaymentIcons";

import { egyptianWalletTypeSchema } from "../schema";
import { MuiTelInput } from "mui-tel-input";
import { EgyptianWalletType } from "../types";

type WalletDetailsEgyptStepFieldsProps<T extends FieldValues> = {
  errors: {
    walletType?: FieldError;
    phoneNumber?: FieldError;
    email?: FieldError;
  };
  register: {
    walletType: UseFormRegisterReturn;
    email: UseFormRegisterReturn;
  };
  control: Control<T>;
};

function WalletDetailsEgyptStepFields<T extends FieldValues>({
  errors,
  register,
  control,
}: WalletDetailsEgyptStepFieldsProps<T>) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<string | null>(null);

  const handleSelect = (value: string) => {
    setSelected(value);
  };

  const walletTypes = {
    vodafone: {
      label: t("pages.payment.overview.v2.receivingDetailsPopUp.vodafone"),
      icon: <VodafoneIcon />,
    },
    orange: {
      label: t("pages.payment.overview.v2.receivingDetailsPopUp.orange"),
      icon: <OrangeIcon />,
    },
    etisalat: {
      label: t("pages.payment.overview.v2.receivingDetailsPopUp.etisalat"),
      icon: <EtisalatIcon />,
    },
    we: {
      label: t("pages.payment.overview.v2.receivingDetailsPopUp.we"),
      icon: <WeIcon />,
    },
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", rowGap: "24px", padding: "0 24px" }}>
      <Typography
        component="h5"
        sx={{
          color: "var(--Gray-600, #475467)",
          fontFamily: "Montserrat",
          fontSize: "18px",
          fontStyle: "normal",
          fontWeight: 400,
          lineHeight: "28px",
        }}
      >
        {t("pages.payment.overview.v2.receivingDetailsPopUp.selectWalletCountryDescription")}
      </Typography>
      <WalletOptions walletTypes={walletTypes} errors={errors} />
      <FormControl
        sx={{
          display: "flex",
          flexDirection: "column",
          rowGap: "6px",
        }}
      >
        <FormLabel
          sx={{
            color: "#344054",
            fontFamily: "Montserrat",
            fontSize: "14px",
            fontStyle: "normal",
            fontWeight: 600,
            lineHeight: "20px",
          }}
        >
          {t("pages.payment.overview.v2.receivingDetailsPopUp.phoneNumberLabel")}
        </FormLabel>
        <Controller
          name={"phoneNumber" as FieldPath<T>}
          control={control}
          render={({ field }) => (
            <MuiTelInput
              {...field}
              defaultCountry="EG"
              error={!!errors.phoneNumber}
              helperText={errors.phoneNumber?.message}
              inputProps={{
                autoComplete: "new-password",
                form: {
                  autoComplete: "off",
                },
              }}
            />
          )}
        />
      </FormControl>

      <FormControl
        sx={{
          display: "flex",
          flexDirection: "column",
          rowGap: "6px",
        }}
      >
        <FormLabel
          sx={{
            color: "#344054",
            fontFamily: "Montserrat",
            fontSize: "14px",
            fontStyle: "normal",
            fontWeight: 600,
            lineHeight: "20px",
          }}
        >
          {t("pages.payment.overview.v2.receivingDetailsPopUp.emailLabel")}
        </FormLabel>
        <TextField {...register.email} error={!!errors.email} helperText={errors.email?.message} />
      </FormControl>
    </Box>
  );
}

export default WalletDetailsEgyptStepFields;

const WalletOptions = ({
  walletTypes,
  errors,
}: {
  walletTypes: {
    vodafone: {
      label: any;
      icon: JSX.Element;
    };
    orange: {
      label: any;
      icon: JSX.Element;
    };
    etisalat: {
      label: any;
      icon: JSX.Element;
    };
    we: {
      label: any;
      icon: JSX.Element;
    };
  };
  errors: {
    walletType?: FieldError;
    phoneNumber?: FieldError;
    email?: FieldError;
  };
}) => {
  const { control } = useFormContext();

  const { field } = useController({
    name: "walletType",
    control,
    defaultValue: "",
  });

  const handleOnWalletTypeSelect = (method: EgyptianWalletType) => field.onChange(method);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "24px" }}>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "repeat(4, 1fr)" }, gap: "24px" }}>
        {egyptianWalletTypeSchema.options.map((walletType) => (
          <Card
            key={walletType}
            sx={{
              flex: 1,
              border:
                field.value === walletType
                  ? "1.5px solid var(--Green---100, #008B07)"
                  : "1.5px solid var(--Gray---100, #E7E7E7)",
              borderRadius: "12px",
              padding: "16px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              position: "relative",
            }}
            onClick={() => handleOnWalletTypeSelect(walletType)}
          >
            <Radio
              sx={{ position: "absolute", top: 0, right: 0 }}
              value={walletType}
              checked={field.value === walletType}
            />
            <CardActionArea
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: "16px",
                "& .MuiCardActionArea-focusHighlight": {
                  backgroundColor: "transparent !important",
                },
              }}
              disableRipple
            >
              {walletTypes[walletType].icon}
              <Typography
                sx={{
                  color: "var(--black-100, #1C1C1C)",
                  fontFamily: "Montserrat",
                  fontSize: "12px",
                  fontStyle: "normal",
                  fontWeight: 500,
                  lineHeight: "28px",
                }}
              >
                {walletTypes[walletType].label}
              </Typography>
            </CardActionArea>
          </Card>
        ))}
      </Box>
      {errors && <FormHelperText error>{errors.walletType?.message}</FormHelperText>}
    </Box>
  );
};
